import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { SERVICE_PROVIDER_UNIT_PARAM, useSpuIdFromUrl } from '../citizen/hooks/use-spu-id-from-url';
import { serviceProviderUnitStorage } from '../citizen/serviceprovider/services/service-provider-unit-storage';
import { ServiceProviderUnitRequiredModal } from '../citizen/serviceprovider/components/service-provider-unit-required-modal';
import { useServiceProviderUnits } from './hooks/use-service-provider-units';

/**
 * Wrapper for non-citizen pages that need a service-provider-unit selection.
 * Mirrors `WithServiceProviderUnitCheck` from the citizen workspace but reads
 * units from the user-scoped `useServiceProviderUnits` hook instead of the
 * citizen context.
 *
 * Priority on mount: existing URL ?spuid → only-unit → favorite from storage → modal prompt.
 */
export function WithUserScopedServiceProviderUnitCheck({ children }: PropsWithChildren<unknown>) {
  const { search } = useLocation();
  const history = useHistory();
  const { units, isLoading } = useServiceProviderUnits();
  const spuId = useSpuIdFromUrl();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (spuId) {
      setShowModal(false);
      return;
    }

    if (isLoading) {
      setShowModal(false);
      return;
    }

    if (!units || units.length === 0) {
      setShowModal(false);
      return;
    }

    if (units.length === 1) {
      const onlyUnitId = units[0].id;
      if (onlyUnitId) {
        writeUrlSpu(onlyUnitId);
      }
      setShowModal(false);
      return;
    }

    const favoriteUnitId = serviceProviderUnitStorage.getFavoriteUnitId();
    const isFavoriteValid = favoriteUnitId && units.some(u => u.id === favoriteUnitId);

    if (isFavoriteValid) {
      writeUrlSpu(favoriteUnitId);
      setShowModal(false);
    } else {
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spuId, isLoading, units, search]);

  function writeUrlSpu(unitId: string) {
    const newParams = new URLSearchParams(search);
    newParams.set(SERVICE_PROVIDER_UNIT_PARAM, unitId);
    history.replace({ search: newParams.toString() });
  }

  function handleSelectUnit(unitId: string) {
    serviceProviderUnitStorage.setFavoriteUnitId(unitId);
    writeUrlSpu(unitId);
  }

  return (
    <>
      {children}
      <ServiceProviderUnitRequiredModal
        open={showModal}
        onClose={() => setShowModal(false)}
        serviceProviderUnits={units}
        onSelectUnit={handleSelectUnit}
      />
    </>
  );
}
